import React, { useEffect, useState, useRef, Fragment, forwardRef, useImperativeHandle } from "react";
import { getDaysBetweenDates, getMonthsBetweenDates, getMonthDays, k_dateType } from './utils'
import clsx from "clsx";
import { MilestoneSvg, ArrowLeftSvg, UndoneMilestoneSvg } from '@/components/svgs'
import { Popover } from 'antd'
import { parseTime } from '@/utils'
import { k_milestoneStatus, getMilestoneStatusLabel } from '../../milestone/edit'
import type { ContractsGant, IGantHandler, PayNodeItem } from './schedule'
import type { MilestoneRender } from '../../milestone/index'
import styles from "./index.module.scss"
import { getStatusBgColor, getStatusTextColor } from "../../paymentNodes/Timeline"

//周/月-刻度显示天；季度-刻度显示周；年度-刻度显示月
// 周-天 100 月-天 40 季-周 140 年-月 200
const enum k_dateWidth {
    week = 158,
    month = 58,
    season = 100,
    year = 158
}


const handleDate = (type: k_dateType = k_dateType.month, start: Date, end: Date) => {
    
    let days = [];
    let currentDate = new Date(start.getTime());
    const allDays = getDaysBetweenDates(start, end)
    const years = []
    const months = []
    
    const startYear = start.getFullYear()
    const startMonth = start.getMonth() + 1
    const startDay = start.getDate()
    if (startDay !== 1) {
        months.push({
            leftCount: 0,
            year: startYear,
            month: startMonth,
            days: getMonthDays(start)
        })
    }
    if (startDay !== 1 || startMonth !== 1) {
        years.push({
            year: startYear,
            leftCount: 0,
            leftStr: "0%"
        })
    }
    const weeks = []
    let weekStart
    let day = 0
    let month = 0
    let year = 0
    while (currentDate <= end) {
        day = currentDate.getDate()
        if (!weekStart) {
            weekStart = day
        }
        if (day === 1) {
            
            let lc = 0
            if (type === k_dateType.month || type === k_dateType.week) {
                lc = days.length
            } else if (type === k_dateType.season) {
                lc = days.length / 7
            } else if (type === k_dateType.year) {
                lc = getMonthsBetweenDates(start, currentDate)
            } else if (type === k_dateType.all) {
                lc = getMonthsBetweenDates(start, currentDate)
            }
            month = currentDate.getMonth() + 1
            year = currentDate.getFullYear()

            if (month === 1) {
                years.push({
                    year: year,
                    leftCount: months.length,
                    leftStr: (getDaysBetweenDates(start, currentDate) / allDays) * 100 + "%"
                })
            }

            months.push({
                leftCount: lc,
                month: month,
                year: year,
                days: getMonthDays(currentDate)
            })
            
        }
        days.push(day);
        if (days.length % 7 === 0) {
            weeks.push({
                start: weekStart,
                end: day
            })
            weekStart = undefined
        }

        currentDate.setDate(day + 1);
    }
    if (weekStart) {
        weeks.push({
            start: weekStart,
            end: end.getDate()
        })
        weekStart = undefined
    }
    return {
        days: days,
        months,
        weeks,
        years
    }
}

interface CalendarMonth{
    leftCount: number, //以当前刻度，左边的刻度数
    month: number,  // 当前月
    year: number,  // 当前年
    days: number,   //本月总天数
}
interface CalendarParams{
    contentW?: number,
    start: number,
    end: number,
    cols: string[],
    months: CalendarMonth[],
    days: number[],
    allDays: number, //所有天数
    weeks: {
        start: number,  // 此周开始的天
        end: number  // 此周结束的天
    }[],
    years: {
        year: number,  // 当前年
        leftCount: number  // 左边的月数
        leftStr: string
    }[]
}
type RenderSubMilestone = RenderDateItem & { milestoneList?: API.Project.Package[] }
interface RenderDateItem{
    title: string,
    start?: string,
    end?: string,
    progress?: number,
    left?: number,
    leftStr?: string,
    widthStr?: string,
    right?: number,
    width?: number,
    days?: number,
    textOut?: boolean,
    textLeft?: boolean,
    dayOut?: boolean,
    showLeftArrow?: boolean,
    showRightArrow?: boolean,
    isMilestone?: boolean,
    isStandard?: boolean,
    isTimeNull?: boolean,
    data?: API.Project.Package,
    // 同一天多个里程碑
    childrens?: RenderSubMilestone[],
}
const getDateWidth = (type: k_dateType) => {
    switch (type) {
        case k_dateType.week:
            return k_dateWidth.week
        case k_dateType.month:
            return k_dateWidth.month
        case k_dateType.season:
            return k_dateWidth.season
        case k_dateType.year:
            return k_dateWidth.year
    }
    return k_dateWidth.month
}
const getStatusColor = (stausID?: number) => {
    if (stausID === 8) {
        return "#4b68ce"
    }
    if (stausID === 5) {
        return "#9ca3af"
    }
    return "#84bcae"
}
interface IProps{
    data: API.Project.Package[],
    contacts: ContractsGant[],
    milestones?: MilestoneRender[]
}
interface PayNode extends PayNodeItem {
    _position: number,
    _leftStr: string
}
interface ContractsGantRender extends API.PROJECT.Contract {
    showLeftArrow?: boolean,
    showRightArrow?: boolean,
    payNodes: PayNode[]
}
interface MilestoneNode extends MilestoneRender{
    _position: number,
    _leftStr: string
}
let throttleTimer = false

const sortByDate = (items: API.Project.Package[]) => {
    items.sort((a, b) => {
        if (a.startDate && b.startDate) {
            return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
        }
        return 0
    })
}

const Gant = forwardRef<IGantHandler, IProps>((props, ref) => {
    const gantEl = useRef<HTMLDivElement>(null)
    const gantBoxEl = useRef<HTMLDivElement>(null)

    const [dateType, setDateType] = useState(k_dateType.all)
    const todayPosition = useRef({
        show: true,
        left: 0,
        leftStr: ""
    })
    const [calendar, setCalendar] = useState<CalendarParams>({
        days: [],  // 所有天的集合
        months: [],  
        weeks: [],
        years: [],
        cols: [],
        start: 0,
        end: 0,
        allDays: 0
    })
    const getPositionByTime =(time: Date, calendarOption?: CalendarParams) => {
        const option = calendarOption || calendar
        const timeMs = time.getTime()
        if (timeMs > option.end || timeMs < option.start) {
            return 0
        }
        const startDate = new Date(option.start)
        const lstart = getDaysBetweenDates(startDate, time)

        let left = 0, midWidth = 0
        switch (dateType) {
            case k_dateType.week:
                left = lstart * k_dateWidth.week
                midWidth = k_dateWidth.week / 2
                break
            case k_dateType.month:
                left = lstart * k_dateWidth.month
                midWidth = k_dateWidth.month / 2
                break
            case k_dateType.season:
                left = (lstart / 7) * k_dateWidth.season
                break
            case k_dateType.year:
                const startMonth = getMonthsBetweenDates(time, startDate)
                const startDays = time.getDate() - 1
                const startMonthDays = getMonthDays(time)

                const startLeft = (startMonth + startDays / startMonthDays) * k_dateWidth.year
                left = startLeft
                break
        }
        return left + midWidth
    }
    const [data, setData] = useState<RenderDateItem[]>([])
    // 合同节点
    const [renderContracts, setRenderContracts] = useState<ContractsGantRender[]>([])
    // 标准里程碑
    const [renderMilestones, setRenderMilestones] = useState<MilestoneNode[]>([])

    useEffect(() => {
        const packageParentIDs = props.data.filter(v => v.typeID !== 2 && v.typeID !== 25).map(v => v.id)

        // “项目开始”、“建筑”、“结构”、“机电”固定占位前四,然后是其他
        
        const newPackages = [...props.data]
        const sorts1: API.Project.Package[] = []
        const sorts2: API.Project.Package[] = []
        const sorts3: API.Project.Package[] = []
        const sorts4: API.Project.Package[] = []
        const others: API.Project.Package[] = []
        newPackages.forEach((v, i) => {
            if (v.subject === "项目开始" || (i === 0 && !v.workPackage)) {
                sorts1.push(v)
                return
            }
            if (v.workPackage) {
                if (v.workPackage.subject === "建筑") {
                    sorts2.push(v)
                    return
                } else if (v.workPackage.subject === "结构") {
                    sorts3.push(v)
                    return
                } else if (v.workPackage.subject === "机电") {
                    sorts4.push(v)
                    return
                }
            }
            others.push(v)
        })
        sortByDate(sorts2)
        sortByDate(sorts3)
        sortByDate(sorts4)
        sortByDate(others)

        const sortedPackages = [...sorts1, ...sorts2, ...sorts3, ...sorts4, ...others]
        
        // typeID为27是专业，不显示
        const packages: RenderDateItem[] = sortedPackages.filter(v => v.typeID !== 27).map(v => {
            let title = v.subject
            // 23,24为标准任务,25为标准里程碑
            const isStandard = [23, 24, 25].includes(v.typeID)
            
            if (isStandard && v.workPackage && v.workPackage.typeID === 27) {
                if (!title.includes("【")) {
                    title = `【${v.workPackage.subject}】${title}`
                }
            }
            return {
                title: title,
                isStandard,
                start: parseTime(v.startDate, "{y}-{m}-{d}"),
                end: parseTime(v.dueDate, "{y}-{m}-{d}"),
                progress: v.derivedDoneRatio || v.doneRatio,
                isMilestone: v.typeID === 2 || v.typeID === 25,
                isTimeNull: !v.startDate || !v.dueDate,
                data: v
            }
        })

        let minDate: number | undefined
        let maxDate: number | undefined
        (packages || []).forEach(v => {
            if(v.isTimeNull) return
            if (v.start) {
                const start = new Date(v.start).getTime()
                if (!minDate) {
                    minDate = start
                } else {
                    minDate = minDate > start ? start : minDate 
                }
            }
            if (v.end) {
                const end = new Date(v.end).getTime()
                if (!maxDate) {
                    maxDate = end
                } else {
                    maxDate = maxDate < end ? end : maxDate
                }
            }
        })
        props.contacts.forEach(contractItem => {
            contractItem.payNodes.forEach(node => {
                if(!node.date) return
                const time = new Date(node.date).getTime()
                if (!minDate) {
                    minDate = time
                } else {
                    minDate = minDate > time ? time : minDate
                }
                if (!maxDate) {
                    maxDate = time
                } else {
                    maxDate = maxDate < time ? time : maxDate
                }
            })
        })
        let initStart = new Date()
        if (!minDate) {
            minDate = initStart.getTime()
        }
        if (!maxDate) {
            const end = new Date(minDate)
            end.setFullYear(end.getFullYear() + 1)
            maxDate = end.getTime()
        }
        if (maxDate - minDate < (1000 * 60 * 60 * 24) * 300) {
            const end = new Date(minDate)
            end.setFullYear(end.getFullYear() + 1)
            maxDate = end.getTime()
        } else {
            const end = new Date(maxDate)
            end.setMonth(end.getMonth() + 2)
            maxDate = end.getTime()
        }
        let tempMinDate = new Date(minDate)
        tempMinDate.setDate(1)
        minDate = tempMinDate.getTime()

       

        const allDays = getDaysBetweenDates(new Date(minDate), new Date(maxDate))
        const gantBoxWidth = gantBoxEl.current?.clientWidth || 500
        let newPackagesData = (packages || []).map(v => {
            if(!minDate || v.isTimeNull) return v
            if (v.start && v.end) {
                const miniDate = new Date(minDate)
                const startDate = new Date(v.start)
                const endDate = new Date(v.end)

                const lstart = getDaysBetweenDates(miniDate, startDate)
                const lend = getDaysBetweenDates(miniDate, endDate)
                const rightDays = getDaysBetweenDates(endDate, new Date(maxDate!))

                let left = 0, right = 0
                let leftStr = "", widthStr = ""
                let width = 0
                let days = lend - lstart >= 0 ? lend - lstart + 1 : 0
                let textLeft = false
                switch (dateType) {
                    case k_dateType.week:
                        left = lstart * k_dateWidth.week
                        width = days * k_dateWidth.week
                        right = left + width
                        break
                    case k_dateType.month:
                        left = lstart * k_dateWidth.month
                        width = days * k_dateWidth.month
                        right = left + width
                        break
                    case k_dateType.season:
                        left = (lstart / 7) * k_dateWidth.season
                        width = (days / 7) * k_dateWidth.season
                        right = left + width
                        break
                    case k_dateType.year:
                        const startMonth = getMonthsBetweenDates(miniDate, startDate)
                        const startDays = startDate.getDate() - 1
                        const startMonthDays = getMonthDays(startDate)

                        const endMonth = getMonthsBetweenDates(miniDate, endDate)
                        const endDays = endDate.getDate()
                        const endMonthDays = getMonthDays(endDate)

                        const endLeft = (endMonth + endDays / endMonthDays) * k_dateWidth.year
                        const startLeft = (startMonth + startDays / startMonthDays) * k_dateWidth.year

                        left = startLeft
                        width = endLeft - startLeft
                        right = left + width
                        break
                    case k_dateType.all:
                        left = (lstart / allDays) * gantBoxWidth
                        width = (days / allDays) * gantBoxWidth
                        leftStr = (lstart / allDays * 100) + "%"
                        widthStr = (days / allDays * 100) + "%"
                        textLeft = (rightDays / allDays) * gantBoxWidth < v.title.length * 16 + 80
                        break
                }

                // let progress = 0
                // const nowDays = getDaysBetweenDates(miniDate, new Date())
                // if (lend <= nowDays) {
                //     progress = 100
                // } else if (lstart > nowDays) {
                //     progress = 0
                // } else {
                //     const curNowDays = getDaysBetweenDates(startDate, new Date())
                //     const curAllDays = getDaysBetweenDates(startDate, endDate)
                //     progress = Math.floor((curNowDays / curAllDays) * 100)
                //     progress = 50
                // }

                // 计算文字是否放外面
                const textOut = v.title.length * 16 + 120 > width
                const dayOut = width < 40

                return {
                    ...v,
                    left,
                    right,
                    leftStr,
                    widthStr,
                    days,
                    width,
                    textOut,
                    textLeft,
                    dayOut
                }
            }
            return v
        })

        // 筛选出子集里程碑
        let parentPackages: RenderDateItem[] = []
        const subMileStones: RenderDateItem[] = []
        newPackagesData.forEach(v => {
            if (!v.isMilestone) {
                parentPackages.push(v)
                return
            }
            if (v.data && packageParentIDs.includes(v.data.parentID)) {
                subMileStones.push(v)
                return
            }
            parentPackages.push(v)
        })
        if (subMileStones.length > 0) {
            parentPackages = parentPackages.map(v => {
                let childs: RenderSubMilestone[] = []
                subMileStones.forEach(sub => {
                    if (sub.data?.parentID === v.data?.id) {
                        childs.push(sub)
                    }
                })
                if (childs.length > 1) {
                    // 多个子里程碑时，判断是否有同一天的
                    const newSubMilestones: RenderSubMilestone[] = []
                    childs.forEach(sub => {
                        const val = newSubMilestones.find(item=>item.start === sub.start && item.end === sub.end)
                        if (!val) {
                            newSubMilestones.push({ ...sub, milestoneList: sub.data ? [sub.data] : [] })
                        } else {
                            sub.data && val.milestoneList!.push(sub.data)
                        }
                    })
                    childs = newSubMilestones
                }
                return {
                    ...v,
                    childrens: childs
                }
            })
        }

        let newArrowData = handleDataArrow(parentPackages)

        setData(newArrowData)

        const date = handleDate(dateType, new Date(minDate), new Date(maxDate))
        let cols: string[] = []
        let contentW = 0
        switch (dateType) {
            case k_dateType.week:
            case k_dateType.month:
                cols = date.days.map(v => `${v}`)
                contentW = cols.length * getDateWidth(dateType)
                break
            case k_dateType.season:
                cols = date.weeks.map(v => {
                    if (v.start === v.end) return `${v.start}日`
                    return `${v.start}日 - ${v.end}日`
                })
                contentW = cols.length * getDateWidth(dateType)
                break
            case k_dateType.year:
                contentW = date.months.length * getDateWidth(dateType)
                break
        }
        const newCalender = {
            ...date,
            cols,
            start: minDate,
            end: maxDate,
            contentW,
            allDays
        }
        const pos = getPositionByTime(new Date(), newCalender)
        const curDate = new Date()
        curDate.setHours(0, 0, 0)
        const curTime = curDate.getTime()
        const curBetweenDays = getDaysBetweenDates(tempMinDate, new Date())
        todayPosition.current = {
            show: curTime >= minDate && curTime <= maxDate,
            left: pos,
            leftStr: (curBetweenDays / allDays) * 100 + "%"
        }
        const newRenderContacts = props.contacts.map(v => {
            const nodes: PayNode[] = (v.payNodes || []).map(node => {
                const lstartDays = getDaysBetweenDates(tempMinDate, new Date(node.date))
                return {
                    ...node, 
                    _position: getPositionByTime(new Date(node.date), newCalender),
                    _leftStr: (lstartDays / allDays) * 100 + "%"
                }
            })
            return {
                ...v,
                payNodes: nodes
            }
        })
        setRenderContracts(newRenderContacts)

        const newMilestones = (props.milestones || []).map(v => {
            let milestoneDate = v.milestoneDate
            const itemDate = new Date(milestoneDate)
            let lstartDays = getDaysBetweenDates(tempMinDate, itemDate)
            const isOver = itemDate.getTime() < tempMinDate.getTime()
            if (isOver) {
                lstartDays = 0
            }
            return {
                ...v,
                _position: milestoneDate ? getPositionByTime(itemDate, newCalender) : 0,
                _leftStr: (lstartDays / allDays) * 100 + "%"
            }
        })

        setRenderMilestones(newMilestones)

        setCalendar(newCalender)
        
    }, [dateType, props.data, props.contacts, props.milestones])

    useEffect(() => {
        scrollToToday()
    }, [calendar])

    const scrollToToday = () => {
        if (!todayPosition.current.show || !gantEl.current || dateType === k_dateType.all) return
        const boxWidth = gantEl.current.clientWidth
        const mid = todayPosition.current.left - boxWidth / 2
        const left = mid < 0 ? todayPosition.current.left : mid
        gantEl.current.scrollTo({
            left: left
        })
    }
    const scrollToDate = (time: Date) => {
        if (!gantEl.current || dateType === k_dateType.all) return
        const timePos = getPositionByTime(time)
        const boxWidth = gantEl.current.clientWidth
        const mid = timePos - boxWidth / 2
        const left = mid < 0 ? timePos : mid
        gantEl.current.scrollTo({
            left: left
        })
    }
    const scrollToItem = (item: RenderDateItem) => {
        if (!item || !gantEl.current || dateType === k_dateType.all) return
        const boxWidth = gantEl.current.clientWidth
        const mid = (item.left || 0) - boxWidth / 2
        const left = mid < 0 ? (item.left || 0) : mid
        gantEl.current.scrollTo({
            left: left
        })
    }
    const scrollLastContract = (item: ContractsGantRender) => {
        if (!gantEl.current || dateType === k_dateType.all) return
        let scrollLeft = gantEl.current.scrollLeft
        const boxWidth = gantEl.current.clientWidth
        const scrollMid = scrollLeft + (boxWidth / 2)
        let scrollVal = 0
        let diff = 99999
        let hasItem = false
        item.payNodes.forEach(node => {
            if (node._position > scrollMid + 10) {
                hasItem = true
                let tempDiff = node._position - scrollMid
                if (tempDiff < diff) {
                    let mid = node._position - boxWidth / 2
                    scrollVal = mid < 0 ? 0 : mid
                    diff = tempDiff
                }
            }
        })
        if(!hasItem) return
        gantEl.current.scrollTo({
            left: scrollVal
        })
    }
    const scrollNextContract = (item: ContractsGantRender) => {
        if (!gantEl.current || dateType === k_dateType.all) return
        let scrollLeft = gantEl.current.scrollLeft
        const boxWidth = gantEl.current.clientWidth
        const scrollMid = scrollLeft + (boxWidth / 2)
        let scrollVal = 0
        let diff = 99999
        let hasItem = false
        item.payNodes.forEach(node => {
            let mid = node._position - boxWidth / 2
            mid = mid < 0 ? 0 : mid
            if (node._position < scrollMid - 10) {
                hasItem = true
                let tempDiff = scrollMid - node._position
                if (tempDiff < diff) {
                    scrollVal = mid
                    diff = tempDiff
                }
            }
        })
        if (!hasItem) return
        gantEl.current.scrollTo({
            left: scrollVal
        })
    }
    const handleDataArrow = (data: RenderDateItem[] = [], scrollLeft: number = 0) => {
        if (!gantEl.current || dateType === k_dateType.all) return data
        if (!scrollLeft) {
            scrollLeft = gantEl.current.scrollLeft
        }
        const boxWidth = gantEl.current.clientWidth
        const scrollRight = scrollLeft + boxWidth

        return data.map(v => {
            let showLeftArrow = false
            let showRightArrow = false
            if (scrollLeft > v.left! + v.width!) {
                showLeftArrow = true
            } else if (scrollRight < v.left!) {
                showRightArrow = true
            }
            return {
                ...v,
                showLeftArrow,
                showRightArrow
            }
        })
    }
    const handleScroll = () => {
        if (!gantEl.current || dateType === k_dateType.all) return

        let scrollLeft = gantEl.current.scrollLeft
        const boxWidth = gantEl.current.clientWidth
        const scrollMid = scrollLeft + (boxWidth / 2)
        const scrollW = gantEl.current.scrollWidth
        // 处理合同节点箭头
        if(renderContracts.length <= 0) return
        setRenderContracts((oldRc)=>oldRc.map(v => {
            return {
                ...v,
                showLeftArrow: (scrollLeft + boxWidth <= scrollW) && v.payNodes.findIndex(node => node._position < scrollMid) > -1,
                showRightArrow: scrollLeft >= 0 && v.payNodes.findIndex(node => node._position > scrollMid) > -1
            }
        }))
    }
    const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
        if (!throttleTimer) {
            throttleTimer = true
            setTimeout(() => {
                const scrollLeft = (e.target as HTMLDivElement).scrollLeft
                setData((val)=>handleDataArrow(val, scrollLeft))
                handleScroll()
                throttleTimer = false
            }, 100);
        }
    }
    useImperativeHandle(ref, () => {
        return {
            scrollToDate: scrollToDate,
        };
    }, [calendar]);
    
    const topHeight = dateType === k_dateType.all ? 35 : 70
    return (
        <div className="w-full">
            <div className="flex items-center justify-between">
                <div className="flex items-center justify-end text-xs">
                    <div className="flex items-center">
                        <div className="bg-[#4b68ce] w-4 h-4 rounded-full inline-flex"></div>
                        <span className="ml-2">已完成</span>
                    </div>
                    <div className="flex items-center ml-6">
                        <div className="bg-[#84bcae] w-4 h-4 rounded-full inline-flex"></div>
                        <span className="ml-2">进行中</span>
                    </div>
                    <div className="flex items-center  ml-6">
                        <div className="bg-gray-400 w-4 h-4 rounded-full inline-flex"></div>
                        <span className="ml-2">未开始</span>
                    </div>
                </div>
                <div className="text-sm gap-1 flex justify-end">
                    <div className={clsx("w-[45px] h-[25px] rounded-sm flex items-center justify-center cursor-pointer hover:bg-blue-200", dateType === k_dateType.all && "bg-blue-200 text-blue-600")} onClick={() => setDateType(k_dateType.all)}>总览</div>
                    <div className={clsx("w-[25px] h-[25px] rounded-sm flex items-center justify-center cursor-pointer hover:bg-blue-200", dateType === k_dateType.week && "bg-blue-200 text-blue-600")} onClick={() => setDateType(k_dateType.week)}>周</div>
                    <div className={clsx("w-[25px] h-[25px] rounded-sm flex items-center justify-center cursor-pointer hover:bg-blue-200", dateType === k_dateType.month && "bg-blue-200 text-blue-600")} onClick={() => setDateType(k_dateType.month)}>月</div>
                    <div className={clsx("w-[25px] h-[25px] rounded-sm flex items-center justify-center cursor-pointer hover:bg-blue-200", dateType === k_dateType.season && "bg-blue-200 text-blue-600")} onClick={() => setDateType(k_dateType.season)}>季</div>
                    <div className={clsx("w-[25px] h-[25px] rounded-sm flex items-center justify-center cursor-pointer hover:bg-blue-200", dateType === k_dateType.year && "bg-blue-200 text-blue-600")} onClick={() => setDateType(k_dateType.year)}>年</div>
                    {todayPosition.current.show && (
                        <div className={clsx("h-[25px] px-2 rounded-sm flex items-center justify-center cursor-pointer hover:bg-blue-200")} onClick={scrollToToday}>今天</div>
                    )}
                </div>
            </div>
            
            <div ref={gantBoxEl} className="w-full relative my-4">
                <div className="w-full relative">
                    <div ref={gantEl} onScroll={onScroll} className="w-full relative overflow-x-auto overflow-y-hidden border-gray-200 border-solid border">
                        <div className="relative" style={{ width: dateType === k_dateType.all ? "100%" : calendar.contentW }}>
                            {(dateType === k_dateType.all || dateType === k_dateType.year) ? (
                                <Fragment>
                                    <div className="w-full relative h-[35px]">
                                        {calendar.years.map((v, i) => (
                                            <div key={i} className="text-gray-500 absolute whitespace-nowrap" style={{ left: dateType === k_dateType.all ? v.leftStr : v.leftCount * getDateWidth(dateType) }}>{v.year}年</div>
                                        ))}
                                    </div>
                                    {(dateType === k_dateType.all && calendar.months.length >= 12) ? (<div className="h-px bg-gray-200 w-full"></div>) : (
                                        <div className="flex border-0 border-b h-[35px] border-gray-200 border-solid">
                                            {calendar.months.map((v, i) => (
                                                <div key={i} className={clsx("text-center text-gray-500 shrink-0", dateType === k_dateType.all && "flex-1")} style={{ width: dateType === k_dateType.all ? "" : getDateWidth(dateType) }}>{v.month}月</div>
                                            ))}
                                        </div>
                                    ) }
                                    
                                    <div>
                                        {dateType !== k_dateType.all && calendar.months.map((v, i) => i > 0 && (
                                            <div key={i} className="absolute w-px bg-gray-200 top-[70px] h-full text-center text-gray-500 shrink-0" style={{ left: getDateWidth(dateType) * i }}></div>
                                        ))}
                                    </div>
                                </Fragment>
                            ) : (
                                <Fragment>
                                    <div className="w-full relative h-[35px]">
                                        {calendar.months.map((v, i) => (
                                            <div key={i} className="text-gray-500 absolute whitespace-nowrap" style={{ left: v.leftCount * getDateWidth(dateType) }}>{v.year}年{v.month}月</div>
                                        ))}
                                    </div>
                                    <div className="flex border-0 border-b h-[35px] border-gray-200 border-solid">
                                        {calendar.cols.map((v, i) => (
                                            <div key={i} className="text-center text-gray-500 shrink-0" style={{ width: getDateWidth(dateType) }}>{v}</div>
                                        ))}
                                    </div>
                                    <div>
                                        {calendar.cols.map((v, i) => i > 0 && (
                                            <div key={i} className="absolute w-px bg-gray-200 top-[70px] h-full text-center text-gray-500 shrink-0" style={{ left: getDateWidth(dateType) * i }}></div>
                                        ))}
                                    </div>
                                </Fragment>
                            )}
                            
                            {data.map((v, i) => (
                                <div key={i} className="relative border-0 border-b border-dashed border-gray-200 w-full h-[40px]">
                                    {v.isTimeNull ? "" : v.isMilestone ? (
                                        <div className="text-[13px] absolute h-[20px] text-white top-[10px] text-center"
                                            style={{
                                                left: dateType === k_dateType.all ? v.leftStr : v.left
                                            }}>
                                            <div className="relative z-10 w-full flex items-center translate-x-[-9px]" style={{ marginLeft: dateType === k_dateType.month || dateType === k_dateType.week ? getDateWidth(dateType) / 2 : 0 }}>
                                                {v.data?.statusID === 8 ? (
                                                    <span className={clsx("text-[18px] flex", v.isStandard ? "text-orange-500" : "text-pink-800")}>
                                                        <MilestoneSvg />
                                                    </span>
                                                ) : (
                                                    <span className={clsx("text-[18px] flex", v.isStandard ? "text-orange-500" : "text-pink-800")}>
                                                        <UndoneMilestoneSvg />
                                                    </span>
                                                )}
                                                <div className="absolute -right-2 top-0 translate-x-full text-gray-700 flex items-center whitespace-nowrap">里程碑 - {v.title}{v.end ? ` - ${v.end}` : ""} {v.data?.user?.lastname ? ` - ${v.data?.user?.lastname}` : ""}</div>
                                            </div>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={clsx("text-[13px] rounded-full absolute h-[20px] text-white top-[10px] text-center", v.isStandard && "border border-orange-500 border-solid")}
                                                style={{
                                                    left: dateType === k_dateType.all ? v.leftStr : v.left,
                                                    width: dateType === k_dateType.all ? v.widthStr : v.width,
                                                    backgroundColor: v.progress && v.progress > 0 ? "#9ca3af" : getStatusColor(v.data?.statusID)
                                                }}>
                                                <div className="relative z-10 w-full flex items-center justify-between px-4 whitespace-nowrap">
                                                    <span>{v.textOut ? '' : v.title}</span>
                                                    <span>{v.dayOut ? '' : v.data?.duration + "天"}</span>
                                                </div>
                                                    <div className={clsx("absolute top-0 text-gray-700 flex items-center whitespace-nowrap", v.textLeft ? "-left-2 -translate-x-full" : "-right-2 translate-x-full")}>
                                                    {v.dayOut && (
                                                    <span className="mr-2">{v.data?.duration}天</span>
                                                    )}
                                                    {v.textOut && (
                                                        <span>{v.title}</span>
                                                    )}
                                                    {v.progress && v.progress > 0 ? (
                                                        <span className="ml-2 text-gray-700">进度{v.progress}%</span>
                                                    ) : ""}
                                                </div>
                                                
                                                {v.progress && v.progress > 0 ? (
                                                    <div className="absolute left-0 top-0 h-full rounded-full"
                                                        style={{ width: `${v.progress}%`, backgroundColor: "#84bcae" }}>

                                                    </div>
                                                ) : ""}
                                            </div>
                                            {v.childrens && v.childrens.length > 0 && v.childrens.map(sub => (
                                                <div key={sub.data?.id} className="text-[13px] absolute h-[20px] text-white top-[10px] text-center"
                                                    style={{
                                                        left: dateType === k_dateType.all ? sub.leftStr : sub.left
                                                    }}>
                                                    <div className="relative z-10 w-full flex items-center translate-x-[-9px]" style={{ marginLeft: dateType === k_dateType.month || dateType === k_dateType.week ? getDateWidth(dateType) / 2 : 0 }}>
                                                        <Popover placement="top"
                                                            content={
                                                                (<div className="flex text-center">
                                                                    <table>
                                                                        <tbody>
                                                                            <tr>
                                                                                <th className="font-bold">阶段里程碑</th>
                                                                                <th className="font-bold">截止日期</th>
                                                                                <th className="font-bold">指定人</th>
                                                                            </tr>
                                                                            {sub.milestoneList?.map((subMile, submile_i) => (
                                                                                <tr key={submile_i}>
                                                                                    <td className="max-w-[160px] p-1">{subMile.subject}</td>
                                                                                    <td className="p-1">{sub.end}</td>
                                                                                    <td className="p-1">{subMile.user?.lastname ? subMile.user?.lastname : ""}</td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>)
                                                            }
                                                        >
                                                            {sub.data?.statusID === 8 ? (
                                                                <span className={clsx("text-[18px] flex", sub.isStandard ? "text-orange-500" : "text-pink-800")}>
                                                                    <MilestoneSvg />
                                                                </span>
                                                            ) : (
                                                                <span className={clsx("text-[18px] flex", sub.isStandard ? "text-orange-500" : "text-pink-800")}>
                                                                    <UndoneMilestoneSvg />
                                                                </span>
                                                            )}
                                                        </Popover>
                                                    </div>
                                                </div>
                                            ))}
                                        </>

                                    )}
                                </div>
                            ))}
                            {renderMilestones.length > 0 && (
                                <div className="relative border-0 border-b border-dashed border-gray-200 w-full h-[40px]">
                                    {renderMilestones.map((v, i) => (
                                        <Popover key={i} content={
                                            <div>
                                                <div className="font-bold flex justify-between">
                                                    <span className="mr-4">{v.milestoneName}</span>
                                                    {getMilestoneStatusLabel(v.status)}
                                                </div>
                                                <div className="mt-2">
                                                    计划完成日期: <span className="text-blue-400 font-bold">{v.milestoneDate}</span>

                                                    {v.outDate && v.status !== k_milestoneStatus.finished && (<>
                                                        , 延期 <span className="text-red-500 font-bold">{v.overDueDays}</span> 天
                                                    </>)}
                                                </div>
                                            </div>
                                        } trigger="hover">
                                            <div key={i} className={clsx("absolute top-[10px]", styles['timeline-item-taild'], v._position <= 0 && styles['is-start'], v.outDate && (v.status === k_milestoneStatus.finished ? styles['is-outdate'] : styles['is-overdue']))}
                                                style={{ left: dateType === k_dateType.all ? v._leftStr : v._position }}></div>
                                        </Popover>

                                    ))}
                                </div>
                            )}
                            {renderContracts.map((v, i) => (
                                <div key={i} className="relative border-0 border-b border-dashed border-gray-200 w-full h-[40px]">
                                    {v.payNodes.map((node, node_i) => (
                                        <Popover placement="top" key={node_i}
                                            content={
                                                (<div className="flex text-center">
                                                    <table>
                                                        <tbody>
                                                            <tr className="font-bold">
                                                                <th className="px-4 py-2">合同</th>
                                                                <th className="px-4 py-2">收款节点</th>
                                                                <th className="px-4 py-2">状态</th>
                                                                <th className="px-4 py-2">描述</th>
                                                            </tr>
                                                            {node.childs.map((subNode, i) => (
                                                                <tr key={i}>
                                                                    <td className="px-4 py-2 max-w-xs text-left">{v.contractName}</td>
                                                                    <td className="px-4 py-2">{subNode.date}</td>
                                                                    <td className={clsx("px-4 py-2", getStatusTextColor(subNode.statusNum))}>{subNode.status}</td>
                                                                    <td className="px-4 py-2 max-w-xs text-left">{subNode.description || "-"}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>)
                                            }
                                        >
                                            <div key={node_i} className={clsx("absolute top-0 h-full w-2 -translate-x-1/2", getStatusBgColor(node.statusNum))} style={{ left: dateType === k_dateType.all ? node._leftStr : node._position }}></div>
                                        </Popover>

                                    ))}
                                </div>
                            ))}
                            {todayPosition.current.show && (
                                <div className="absolute h-full w-px z-10 bg-blue-500" style={{ left: dateType === k_dateType.all ? todayPosition.current.leftStr : todayPosition.current.left, top: topHeight }}>
                                    <div className="absolute w-[5px] h-[5px] rounded-full bg-blue-500 top-0 left-[-2px]"></div>
                                </div>
                            )}
                        </div>
                    </div>
                    {data.map((v, i) => (
                        <div key={`data-${i}`} className="absolute left-0 right-0 top-0 w-full">
                            {v.isTimeNull && (
                                <div className="pointer-events-none absolute z-20 left-0 right-0 flex items-center justify-center text-gray-400/40 tracking-[0.5rem]" style={{ top: topHeight + i * 40 + 10 }}>
                                    {v.title}
                                </div>
                            )}
                            {!v.isTimeNull && v.showLeftArrow && dateType !== k_dateType.all && (
                                <div onClick={() => scrollToItem(v)} className="group absolute cursor-pointer z-20 rounded-sm left-4 w-[20px] h-[20px] flex items-center justify-center bg-gray-300 text-black hover:bg-blue-500 hover:text-white"
                                    style={{ top: topHeight + i * 40 + 10 }}>
                                    <span className="flex">
                                        <ArrowLeftSvg />
                                    </span>
                                    <div className="hidden group-hover:flex absolute left-[25px] whitespace-nowrap text-gray-700">{v.start} ~ {v.end}</div>
                                </div>
                            )}
                            {!v.isTimeNull && v.showRightArrow && dateType !== k_dateType.all && (
                                <div onClick={() => scrollToItem(v)} className="group absolute cursor-pointer z-20 rounded-sm right-4 w-[20px] h-[20px] flex items-center justify-center bg-gray-300 text-black hover:bg-blue-500 hover:text-white"
                                    style={{ top: topHeight + i * 40 + 10 }}>
                                    <span className="flex rotate-180">
                                        <ArrowLeftSvg />
                                    </span>
                                    <div className="hidden group-hover:flex absolute right-[25px] whitespace-nowrap text-gray-700">{v.start} ~ {v.end}</div>
                                </div>
                            )}
                        </div>
                    ))}
                    {renderContracts.map((v, i) => (
                        <div className="absolute left-0 right-0 top-0 w-full" key={`contants-${i}`}>
                            <div className="pointer-events-none absolute z-20 left-0 right-0 flex items-center justify-center text-red-800/40 tracking-[0.5rem] whitespace-nowrap overflow-hidden select-none"
                                style={{ top: topHeight + ((data.length) * 40) + i * 40 + 10 + (renderMilestones.length > 0 ? 40 : 0) }}>
                                {v.contractName}
                            </div>
                            {v.showLeftArrow && dateType !== k_dateType.all && (
                                <div onClick={() => scrollNextContract(v)} className="group absolute cursor-pointer z-20 rounded-sm left-4 w-[20px] h-[20px] flex items-center justify-center bg-gray-300 text-black hover:bg-blue-500 hover:text-white"
                                    style={{ top: topHeight + ((data.length) * 40) + i * 40 + 10 }}>
                                    <span className="flex">
                                        <ArrowLeftSvg />
                                    </span>
                                </div>
                            )}
                            {v.showRightArrow && dateType !== k_dateType.all && (
                                <div onClick={() => scrollLastContract(v)} className="group absolute cursor-pointer z-20 rounded-sm right-4 w-[20px] h-[20px] flex items-center justify-center bg-gray-300 text-black hover:bg-blue-500 hover:text-white"
                                    style={{ top: topHeight + ((data.length) * 40) + i * 40 + 10 }}>
                                    <span className="flex rotate-180">
                                        <ArrowLeftSvg />
                                    </span>
                                </div>
                            )}
                        </div>

                    ))}
                </div>
                
            </div>
        </div>
        
    )
})
export default Gant